/**
 * Private Suite First-Time Onboarding
 * 
 * "A gentle arrival"
 * 
 * This is NOT explaining features.
 * This is creating a felt experience in under 30 seconds.
 * 
 * Design Principles:
 * - Slow transitions (0.5-1.2s fades)
 * - Minimal UI, no clutter
 * - Emotional pacing with micro pauses
 * - No explanations, just feeling
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

// Onboarding phases
const PHASES = {
  ARRIVAL: 'arrival',
  FEELING: 'feeling',
  DESIRED: 'desired',
  TRANSITION: 'transition',
  COMPLETE: 'complete'
};

// Current state options
const CURRENT_FEELINGS = [
  { id: 'stressed', label: 'Stressed' },
  { id: 'low_energy', label: 'Low energy' },
  { id: 'distracted', label: 'Distracted' },
  { id: 'anxious', label: 'Anxious' },
  { id: 'unfocused', label: 'Unfocused' },
  { id: 'tired', label: 'Tired' }
];

// Desired state options
const DESIRED_FEELINGS = [
  { id: 'calm', label: 'Calm' },
  { id: 'focus', label: 'Focus' },
  { id: 'confidence', label: 'Confidence' },
  { id: 'energy', label: 'Energy' },
  { id: 'presence', label: 'Presence' },
  { id: 'clarity', label: 'Clarity' }
];

const PrivateSuiteOnboarding = ({ 
  onComplete,
  onSkip 
}) => {
  const [phase, setPhase] = useState(PHASES.ARRIVAL);
  const [arrivalStep, setArrivalStep] = useState(0);
  const [currentFeeling, setCurrentFeeling] = useState(null);
  const [desiredFeeling, setDesiredFeeling] = useState(null);
  const [transitionStep, setTransitionStep] = useState(0);

  // Arrival text animation sequence
  useEffect(() => {
    if (phase === PHASES.ARRIVAL) {
      const timers = [];
      
      // Step 0: "This is your space" - shows immediately
      // Step 1: "Nothing here is fixed" - after 1.5s
      timers.push(setTimeout(() => setArrivalStep(1), 1500));
      // Step 2: "Everything responds to you" - after 3s
      timers.push(setTimeout(() => setArrivalStep(2), 3000));
      // Step 3: Second block - after 5s
      timers.push(setTimeout(() => setArrivalStep(3), 5000));
      // Step 4: Show CTA - after 7s
      timers.push(setTimeout(() => setArrivalStep(4), 7000));
      
      return () => timers.forEach(t => clearTimeout(t));
    }
  }, [phase]);

  // Transition animation sequence
  useEffect(() => {
    if (phase === PHASES.TRANSITION) {
      const timers = [];
      
      // Step 0: "Good" - shows immediately
      // Step 1: "we'll create something just for you" - after 1.5s
      timers.push(setTimeout(() => setTransitionStep(1), 1500));
      // Step 2: "Let yourself soften into it" - after 3.5s
      timers.push(setTimeout(() => setTransitionStep(2), 3500));
      // Step 3: Complete - after 5.5s
      timers.push(setTimeout(() => {
        // Pass selections to parent and complete
        onComplete({
          currentFeeling,
          desiredFeeling,
          isFirstTime: true
        });
      }, 5500));
      
      return () => timers.forEach(t => clearTimeout(t));
    }
  }, [phase, currentFeeling, desiredFeeling, onComplete]);

  const handleFeelingSelect = (feeling) => {
    setCurrentFeeling(feeling);
    // Brief pause before moving to next phase
    setTimeout(() => setPhase(PHASES.DESIRED), 400);
  };

  const handleDesiredSelect = (feeling) => {
    setDesiredFeeling(feeling);
    // Move to transition phase
    setTimeout(() => setPhase(PHASES.TRANSITION), 400);
  };

  return (
    <div className="fixed inset-0 bg-[#0a0a12] z-[100] flex items-center justify-center overflow-hidden">
      {/* Subtle ambient glow */}
      <div 
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(147, 51, 234, 0.15) 0%, transparent 60%)'
        }}
      />
      
      <AnimatePresence mode="wait">
        {/* PHASE 1: ARRIVAL */}
        {phase === PHASES.ARRIVAL && (
          <motion.div
            key="arrival"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center px-8 max-w-md"
          >
            <div className="space-y-4 mb-12">
              {/* Line 1 */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-white/90 text-2xl font-light leading-relaxed"
              >
                This is your space
              </motion.p>
              
              {/* Line 2 */}
              <AnimatePresence>
                {arrivalStep >= 1 && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-white/60 text-lg leading-relaxed"
                  >
                    Nothing here is fixed
                  </motion.p>
                )}
              </AnimatePresence>
              
              {/* Line 3 */}
              <AnimatePresence>
                {arrivalStep >= 2 && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-white/60 text-lg leading-relaxed"
                  >
                    Everything responds to you
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
            
            {/* Second block */}
            <AnimatePresence>
              {arrivalStep >= 3 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1 }}
                  className="space-y-3 mb-12"
                >
                  <p className="text-white/40 text-base">
                    There's nothing to figure out
                  </p>
                  <p className="text-white/40 text-base">
                    Just begin where you are
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* CTA */}
            <AnimatePresence>
              {arrivalStep >= 4 && (
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  onClick={() => setPhase(PHASES.FEELING)}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full 
                           bg-white/10 border border-white/20 text-white
                           hover:bg-white/15 transition-all duration-300"
                  data-testid="onboarding-begin"
                >
                  Begin
                  <ArrowRight size={18} />
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* PHASE 2: CURRENT FEELING */}
        {phase === PHASES.FEELING && (
          <motion.div
            key="feeling"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center px-6 max-w-md w-full"
          >
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-white text-2xl font-light mb-3"
            >
              What are you feeling right now?
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-white/30 text-sm mb-10"
            >
              Just notice… nothing to change
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="grid grid-cols-2 gap-3"
            >
              {CURRENT_FEELINGS.map((feeling, index) => (
                <motion.button
                  key={feeling.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.08, duration: 0.4 }}
                  onClick={() => handleFeelingSelect(feeling.id)}
                  className={`p-5 rounded-2xl text-left transition-all duration-300
                    ${currentFeeling === feeling.id 
                      ? 'bg-white/15 border border-white/30 scale-105' 
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                    }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`feeling-${feeling.id}`}
                >
                  <p className="text-white/80 font-medium">{feeling.label}</p>
                </motion.button>
              ))}
            </motion.div>
          </motion.div>
        )}

        {/* PHASE 3: DESIRED FEELING */}
        {phase === PHASES.DESIRED && (
          <motion.div
            key="desired"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center px-6 max-w-md w-full"
          >
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-white text-2xl font-light mb-3"
            >
              What would you like to feel instead?
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-white/30 text-sm mb-10"
            >
              Let yourself choose simply
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="grid grid-cols-2 gap-3"
            >
              {DESIRED_FEELINGS.map((feeling, index) => (
                <motion.button
                  key={feeling.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.08, duration: 0.4 }}
                  onClick={() => handleDesiredSelect(feeling.id)}
                  className={`p-5 rounded-2xl text-left transition-all duration-300
                    ${desiredFeeling === feeling.id 
                      ? 'bg-violet-500/20 border border-violet-500/40 scale-105' 
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                    }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`desired-${feeling.id}`}
                >
                  <p className="text-white/80 font-medium">{feeling.label}</p>
                </motion.button>
              ))}
            </motion.div>
          </motion.div>
        )}

        {/* PHASE 4: TRANSITION (The Micro Moment) */}
        {phase === PHASES.TRANSITION && (
          <motion.div
            key="transition"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center px-8 max-w-md"
          >
            {/* Subtle violet glow intensifies */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              style={{
                background: 'radial-gradient(ellipse at 50% 50%, rgba(147, 51, 234, 0.2) 0%, transparent 50%)'
              }}
            />
            
            <div className="relative space-y-6">
              {/* "Good" */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                className="text-white/90 text-2xl font-light"
              >
                Good
              </motion.p>
              
              {/* "we'll create something just for you" */}
              <AnimatePresence>
                {transitionStep >= 1 && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-white/60 text-lg"
                  >
                    we'll create something just for you
                  </motion.p>
                )}
              </AnimatePresence>
              
              {/* "Let yourself soften into it" */}
              <AnimatePresence>
                {transitionStep >= 2 && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                    className="text-white/40 text-base mt-8"
                  >
                    Let yourself soften into it
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Skip button - very subtle, bottom corner */}
      {phase === PHASES.ARRIVAL && arrivalStep < 4 && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          onClick={onSkip}
          className="absolute bottom-8 right-8 text-white/20 text-xs hover:text-white/40 transition-colors"
        >
          Skip
        </motion.button>
      )}
    </div>
  );
};

export default PrivateSuiteOnboarding;
